import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { reveal } from "./constants";

const JOBS = [
  {
    id: "white-guards-glenwood",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/x2y1plj2_White%20Gutter%20Guards%20Glenwood.jpeg",
    alt: "White perforated gutter guards installed along a Glenwood home's roofline",
    tag: "Gutter Covers Installed",
    location: "Glenwood, IA",
    caption:
      "Fresh White Gutter Cover install. Perforated screen lets water through and keeps leaves on top.",
  },
  {
    id: "black-guards-glenwood",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/8ou7obxz_Black%20Gutter%20Guards%20Glenwood.jpeg",
    alt: "Black gutter guards installed along a Glenwood home with a residential street view",
    tag: "Gutter Covers Installed",
    location: "Glenwood, IA",
    caption:
      "Black gutter covers blending right into the roofline — looks built-in from the ground.",
  },
  {
    id: "black-guards-3-year",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/q32h6fb2_Black%20Gutter%20Guards%203%20Year%20Inspection.jpeg",
    alt: "Hand inspecting a black gutter guard three years after installation",
    tag: "3-Year Inspection",
    location: "Glenwood, IA",
    caption:
      "Same guard system three years later. Just fine dust on top — still flowing clean underneath.",
  },
  {
    id: "mike-customer-shingles-pristine",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/j9hhazm2_image.png",
    alt: "Spotless white residential gutter alongside dark shingled roof after professional cleaning",
    tag: "Gutter Cleaning — After",
    location: "Southwest Iowa",
    caption:
      "Customer Mike's place — gutter line completely cleared. Shingles dry, no debris, flowing clean.",
  },
  {
    id: "mike-customer-valley-cleared",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/yaup8y8c_image.png",
    alt: "Cleaned residential gutter running along a freshly-cleared shingled roofline",
    tag: "Gutter Cleaning — After",
    location: "Southwest Iowa",
    caption:
      "Another after shot from Mike's place. Hauled all the debris off. \"Will use again.\" — Mike, Google review.",
  },
  {
    id: "torn-window-screen",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/yqo4eo6c_image13.jpeg",
    alt: "Hand showing a tear in a brown residential window screen mesh",
    tag: "Window Screen Repair",
    location: "Glenwood, IA",
    caption:
      "Torn screen letting bugs straight in. Quick mesh replacement and the window's back in business.",
  },
  {
    id: "clogged-gutter-debris",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/khzusfe9_image12.jpeg",
    alt: "Close-up of a yellow gutter packed with rotted leaves, dirt, and standing debris",
    tag: "Gutter Cleaning — Before",
    location: "Glenwood, IA",
    caption:
      "Years of packed muck, roof grit, and rotted leaves. This is what we cleared out — flow restored, downspouts moving water again.",
  },
  {
    id: "roof-valley-debris",
    image:
      "https://customer-assets.emergentagent.com/job_gutters-sw-iowa/artifacts/2f3ywg9d_image9.jpeg",
    alt: "Roof valley packed with sticks, dead leaves and debris before cleaning",
    tag: "Gutter & Valley Clean-Out",
    location: "Glenwood, IA",
    caption:
      "Roof valley packed with sticks and leaves — exactly where water needs a clear path. All cleared, gutter back to draining like it should.",
  },
];

const ROTATE_MS = 5500;

export function RecentJobs() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Auto-rotate
  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(
      () => setIndex((i) => (i + 1) % JOBS.length),
      ROTATE_MS
    );
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  const goTo = (next) => {
    setIndex(((next % JOBS.length) + JOBS.length) % JOBS.length);
  };

  const current = JOBS[index];

  return (
    <section
      id="recent-jobs"
      className="relative py-12 sm:py-16 bg-[#FFFFFF] border-t border-[#E5E5E5]"
      data-testid="recent-jobs-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...reveal} className="max-w-2xl mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-4">
            <span className="w-6 h-px bg-[#5FBE1F]" />
            Recent jobs
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-normal text-[#000000] leading-[1.12]">
            Real work, real <span className="text-[#5FBE1F]">Glenwood homes</span>.
          </h2>
          <p className="mt-3 text-[#4A4A46] text-base sm:text-lg">
            A look at recent gutter cover installs around Southwest Iowa.
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Image stage */}
          <div
            className="relative lg:col-span-8 overflow-hidden bg-[#0A0A0A] rounded-sm aspect-[4/3] sm:aspect-[16/10]"
            data-testid="recent-jobs-stage"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={current.id}
                src={current.image}
                alt={current.alt}
                loading="lazy"
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>

            {/* Pagination dots overlay */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              {JOBS.map((j, i) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Show job ${i + 1}`}
                  data-testid={`recent-jobs-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index
                      ? "w-8 bg-[#5FBE1F]"
                      : "w-3 bg-white/60 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Caption + controls */}
          <div className="lg:col-span-4 flex flex-col justify-between">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id + "-caption"}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45 }}
                className="space-y-3"
              >
                <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5FBE1F]">
                  <span className="w-4 h-px bg-[#5FBE1F]" />
                  {current.tag}
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-[#000000] leading-tight">
                  {current.caption}
                </h3>
                <div className="flex items-center gap-2 text-sm text-[#4A4A46]">
                  <MapPin className="w-4 h-4 text-[#0A0A0A]" />
                  <span>{current.location}</span>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0A0A0A]">
                {String(index + 1).padStart(2, "0")}
                <span className="text-[#9CA09B]"> / {String(JOBS.length).padStart(2, "0")}</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => goTo(index - 1)}
                  aria-label="Previous job"
                  data-testid="recent-jobs-prev"
                  className="w-10 h-10 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => goTo(index + 1)}
                  aria-label="Next job"
                  data-testid="recent-jobs-next"
                  className="w-10 h-10 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
